import { create } from 'zustand'
import { api } from '../services/api'
import { useAuthStore } from './authStore'
import type { Zone, UnitType, UnitStatus } from '@territory-nexus/shared'

interface PlayerStats {
  zonesOwned: number
  unitsDeployed: number
  battlesWon: number
}

interface PlayerResources {
  energy: number
  nanomaterials: number
  quantum: number
  darkMatter: number
}

export interface Unit {
  id: string
  playerId: string
  unitType: UnitType
  level: number
  xp: number
  stats: { hp: number; atk: number; def: number; spd: number; acc: number; energy: number }
  trait: string | null
  zoneId: string | null
  status: UnitStatus
  currentHp: number | null
  createdAt: Date
}

interface GameState {
  // Zone state
  currentZone: Zone | null
  nearbyZones: Zone[]
  playerZones: Zone[]
  isLoadingZones: boolean
  zoneError: string | null

  // Location
  playerLocation: { lat: number; lng: number } | null

  // Player stats
  playerStats: PlayerStats

  // Resources
  resources: PlayerResources
  isLoadingResources: boolean

  // Units
  units: Unit[]
  isLoadingUnits: boolean

  // Actions
  setLocation: (lat: number, lng: number) => void
  setCurrentZone: (zone: Zone | null) => void
  fetchNearbyZones: (lat: number, lng: number, radius?: number) => Promise<void>
  fetchPlayerZones: () => Promise<void>
  claimZone: (hexId: string, lat: number, lng: number) => Promise<Zone>
  abandonZone: (zoneId: string) => Promise<void>
  updatePlayerStats: () => void
  fetchResources: () => Promise<void>

  fetchUnits: () => Promise<void>
  createUnit: (unitType: UnitType) => Promise<Unit>
  deployUnit: (unitId: string, zoneId: string) => Promise<Unit>
  recallUnit: (unitId: string) => Promise<Unit>
  disbandUnit: (unitId: string) => Promise<void>
}

export const useGameStore = create<GameState>((set, get) => ({
  // Initial state
  currentZone: null,
  nearbyZones: [],
  playerZones: [],
  isLoadingZones: false,
  zoneError: null,
  playerLocation: null,
  playerStats: {
    zonesOwned: 0,
    unitsDeployed: 0,
    battlesWon: 0,
  },
  resources: {
    energy: 0,
    nanomaterials: 0,
    quantum: 0,
    darkMatter: 0,
  },
  isLoadingResources: false,
  units: [],
  isLoadingUnits: false,
  setLocation: (lat, lng) => set({ playerLocation: { lat, lng } }),

  setCurrentZone: (zone) => set({ currentZone: zone }),

  fetchNearbyZones: async (lat: number, lng: number, radius = 500) => {
    set({ isLoadingZones: true, zoneError: null })
    try {
      const response = await api.get<{ success: boolean; data: Zone[] }>('/zones', {
        params: { lat, lng, radius },
      })
      if (response.data.success) {
        set({ nearbyZones: response.data.data, isLoadingZones: false })
      }
    } catch (error: unknown) {
      console.error('Failed to fetch nearby zones:', error)
      const err = error as { response?: { data?: { error?: string } } }
      set({
        zoneError: err.response?.data?.error || 'Failed to fetch zones',
        isLoadingZones: false,
      })
    }
  },

  fetchPlayerZones: async () => {
    try {
      const response = await api.get<{ success: boolean; data: Zone[] }>('/zones', {
        params: { owned: 'true' },
      })
      if (response.data.success) {
        set({ playerZones: response.data.data })
        get().updatePlayerStats()
      }
    } catch (error: unknown) {
      console.error('Failed to fetch player zones:', error)
    }
  },

  claimZone: async (hexId: string, lat: number, lng: number) => {
    set({ isLoadingZones: true, zoneError: null })
    try {
      const response = await api.post<{ success: boolean; data: Zone }>('/zones/claim', {
        hexId,
        lat,
        lng,
      })
      if (response.data.success) {
        const newZone = response.data.data
        set((state) => ({
          playerZones: [...state.playerZones, newZone],
          currentZone: newZone,
          isLoadingZones: false,
        }))
        get().updatePlayerStats()
        // Refresh nearby zones to update statuses
        get().fetchNearbyZones(lat, lng)
        // Refresh player data (XP/level may have changed)
        useAuthStore.getState().refreshPlayer()
        return newZone
      }
      throw new Error('Failed to claim zone')
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } }
      const errorMsg = err.response?.data?.error || 'Failed to claim zone'
      set({ zoneError: errorMsg, isLoadingZones: false })
      throw new Error(errorMsg)
    }
  },

  abandonZone: async (zoneId: string) => {
    try {
      await api.delete(`/zones/${zoneId}`)
      set((state) => ({
        playerZones: state.playerZones.filter((z) => z.id !== zoneId),
        currentZone: state.currentZone?.id === zoneId ? null : state.currentZone,
      }))
      get().updatePlayerStats()
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } }
      const errorMsg = err.response?.data?.error || 'Failed to abandon zone'
      set({ zoneError: errorMsg })
      throw new Error(errorMsg)
    }
  },

  updatePlayerStats: () => {
    const { playerZones, units } = get()
    set({
      playerStats: {
        zonesOwned: playerZones.length,
        unitsDeployed: units.filter((u) => u.status === 'deployed').length,
        battlesWon: 0, // TODO: fetch from battles
      },
    })
  },

  fetchResources: async () => {
    set({ isLoadingResources: true })
    try {
      const response = await api.get<{
        success: boolean
        data: {
          energy: number
          nanomaterials: number
          quantum: number
          darkMatter: number
        }
      }>('/resources')
      if (response.data.success) {
        const data = response.data.data
        set({
          resources: {
            energy: data.energy,
            nanomaterials: data.nanomaterials,
            quantum: data.quantum,
            darkMatter: data.darkMatter,
          },
          isLoadingResources: false,
        })
      }
    } catch (error: unknown) {
      console.error('Failed to fetch resources:', error)
      set({ isLoadingResources: false })
    }
  },

  fetchUnits: async () => {
    set({ isLoadingUnits: true })
    try {
      const response = await api.get<{ success: boolean; data: Unit[] }>('/units')
      if (response.data.success) {
        set({ units: response.data.data, isLoadingUnits: false })
        get().updatePlayerStats()
      }
    } catch (error: unknown) {
      console.error('Failed to fetch units:', error)
      set({ isLoadingUnits: false })
    }
  },

  createUnit: async (unitType: UnitType) => {
    try {
      const response = await api.post<{ success: boolean; data: Unit }>('/units', { unitType })
      if (response.data.success) {
        const newUnit = response.data.data
        set((state) => ({ units: [newUnit, ...state.units] }))
        get().fetchResources() // Refresh resources after spending
        useAuthStore.getState().refreshPlayer()
        return newUnit
      }
      throw new Error('Failed to create unit')
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } }
      const errorMsg = err.response?.data?.error || 'Failed to create unit'
      throw new Error(errorMsg)
    }
  },

  deployUnit: async (unitId: string, zoneId: string) => {
    try {
      const response = await api.post<{ success: boolean; data: Unit }>(`/units/${unitId}/deploy`, { zoneId })
      if (response.data.success) {
        const updatedUnit = response.data.data
        set((state) => ({
          units: state.units.map((u) => (u.id === unitId ? updatedUnit : u)),
        }))
        get().updatePlayerStats()
        return updatedUnit
      }
      throw new Error('Failed to deploy unit')
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } }
      const errorMsg = err.response?.data?.error || 'Failed to deploy unit'
      throw new Error(errorMsg)
    }
  },

  recallUnit: async (unitId: string) => {
    try {
      const response = await api.post<{ success: boolean; data: Unit }>(`/units/${unitId}/recall`)
      if (response.data.success) {
        const updatedUnit = response.data.data
        set((state) => ({
          units: state.units.map((u) => (u.id === unitId ? updatedUnit : u)),
        }))
        get().updatePlayerStats()
        return updatedUnit
      }
      throw new Error('Failed to recall unit')
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } }
      const errorMsg = err.response?.data?.error || 'Failed to recall unit'
      throw new Error(errorMsg)
    }
  },

  disbandUnit: async (unitId: string) => {
    try {
      await api.delete(`/units/${unitId}`)
      set((state) => ({ units: state.units.filter((u) => u.id !== unitId) }))
      get().updatePlayerStats()
      get().fetchResources()
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } }
      const errorMsg = err.response?.data?.error || 'Failed to disband unit'
      throw new Error(errorMsg)
    }
  },

}))
